'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { 
  Image, 
  Wand2,
  Copy,
  CheckCircle,
  Save,
  Download,
  Upload,
  Trash2,
  Eye,
  Plus,
  X,
  Palette,
  Type,
  Sparkles,
  Grid3X3,
  Layout,
  Camera
} from 'lucide-react'
import { Brand } from '../../utils/mockData'

interface MoodBoardCreatorProps {
  brand: Brand
}

interface MoodBoardItem {
  id: string
  type: 'image' | 'color' | 'typography' | 'texture' | 'inspiration'
  title: string
  description: string
  source?: string
  file?: File | null
  preview?: string
  color?: string
  fontFamily?: string
  tags: string[]
}

interface MoodBoard {
  title: string
  description: string
  objective: string
  items: MoodBoardItem[]
  styleKeywords: string[]
  colorPalette: string[]
  typography: string[]
  notes: string
}

const STYLE_KEYWORDS = [
  'Modern', 'Minimalist', 'Bold', 'Elegant', 'Playful', 'Professional',
  'Rustic', 'Vintage', 'Futuristic', 'Organic', 'Geometric', 'Luxurious',
  'Friendly', 'Sophisticated', 'Energetic', 'Calm', 'Innovative', 'Traditional',
  'Artistic', 'Clean', 'Warm', 'Cool', 'Vibrant', 'Subtle'
]

const INSPIRATION_CATEGORIES = [
  { name: 'Colors', icon: Palette, description: 'Color combinations and palettes' },
  { name: 'Typography', icon: Type, description: 'Font styles and text treatments' },
  { name: 'Photography', icon: Camera, description: 'Photo styles and imagery' },
  { name: 'Layout', icon: Layout, description: 'Design layouts and compositions' },
  { name: 'Textures', icon: Grid3X3, description: 'Surface textures and patterns' },
  { name: 'Concepts', icon: Sparkles, description: 'Creative concepts and ideas' }
]

const SAMPLE_MOOD_ITEMS: MoodBoardItem[] = [
  {
    id: 'sample-1',
    type: 'color',
    title: 'Ocean Blue Palette',
    description: 'Calming blue tones reminiscent of ocean depths',
    color: '#1e40af',
    tags: ['blue', 'calming', 'professional']
  },
  {
    id: 'sample-2',
    type: 'typography',
    title: 'Modern Sans Serif',
    description: 'Clean, geometric typography for tech brands',
    fontFamily: 'Inter',
    tags: ['modern', 'clean', 'tech']
  },
  {
    id: 'sample-3',
    type: 'inspiration',
    title: 'Minimalist Interface',
    description: 'Clean interface design with lots of white space',
    tags: ['minimalist', 'clean', 'ui']
  }
]

export function MoodBoardCreator({ brand }: MoodBoardCreatorProps) {
  const [moodBoard, setMoodBoard] = useState<MoodBoard>({
    title: `${brand.name} Mood Board`,
    description: '',
    objective: 'Define the visual direction and aesthetic for the brand',
    items: [...SAMPLE_MOOD_ITEMS],
    styleKeywords: [],
    colorPalette: [],
    typography: [],
    notes: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [newItem, setNewItem] = useState<Partial<MoodBoardItem>>({
    title: '',
    description: '',
    type: 'inspiration',
    tags: []
  })
  const [newTag, setNewTag] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  useEffect(() => {
    loadMoodBoardData()
  }, [brand.id])

  const loadMoodBoardData = async () => {
    setIsLoading(true)
    try {
      const savedData = localStorage.getItem(`moodBoard_${brand.id}`)
      if (savedData) {
        setMoodBoard(JSON.parse(savedData))
      }
    } catch (error) {
      console.error('Error loading mood board data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveMoodBoardData = async () => {
    setIsSaving(true)
    try {
      localStorage.setItem(`moodBoard_${brand.id}`, JSON.stringify(moodBoard))
      setLastSaved(new Date())
    } catch (error) {
      console.error('Error saving mood board data:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const preview = URL.createObjectURL(file)
      setNewItem(prev => ({ ...prev, file, preview }))
    }
  }

  const addMoodBoardItem = () => {
    if (!newItem.title?.trim()) return

    const item: MoodBoardItem = {
      id: `item-${Date.now()}`,
      type: newItem.type || 'inspiration',
      title: newItem.title,
      description: newItem.description || '',
      file: newItem.file,
      preview: newItem.preview,
      color: newItem.color,
      fontFamily: newItem.fontFamily,
      tags: newItem.tags || []
    }

    setMoodBoard(prev => ({
      ...prev,
      items: [...prev.items, item]
    }))

    setNewItem({
      title: '',
      description: '',
      type: 'inspiration',
      tags: []
    })
  }

  const removeMoodBoardItem = (id: string) => {
    setMoodBoard(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }))
  }

  const toggleStyleKeyword = (keyword: string) => {
    setMoodBoard(prev => ({
      ...prev,
      styleKeywords: prev.styleKeywords.includes(keyword)
        ? prev.styleKeywords.filter(k => k !== keyword)
        : [...prev.styleKeywords, keyword]
    }))
  }

  const addColorToPalette = (color: string) => {
    if (!color || moodBoard.colorPalette.includes(color)) return
    
    setMoodBoard(prev => ({
      ...prev,
      colorPalette: [...prev.colorPalette, color]
    }))
  }

  const removeColorFromPalette = (color: string) => {
    setMoodBoard(prev => ({
      ...prev,
      colorPalette: prev.colorPalette.filter(c => c !== color)
    }))
  }

  const addTagToItem = (tag: string) => {
    if (!tag.trim()) return
    
    setNewItem(prev => ({
      ...prev,
      tags: [...(prev.tags || []), tag.trim()]
    }))
    setNewTag('')
  }

  const removeTagFromItem = (index: number) => {
    setNewItem(prev => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index) || []
    }))
  }

  const generateAIMoodBoard = async () => {
    setIsLoading(true)
    try {
      // Mock AI mood board generation based on brand industry
      const industryMoodBoards = {
        'Technology': {
          styleKeywords: ['Modern', 'Clean', 'Innovative', 'Professional'],
          colorPalette: ['#2563eb', '#3b82f6', '#60a5fa', '#1e293b', '#f1f5f9'],
          items: [
            {
              id: 'ai-tech-1',
              type: 'color' as const,
              title: 'Tech Blue Gradient',
              description: 'Modern blue gradient representing innovation',
              color: '#2563eb',
              tags: ['blue', 'gradient', 'modern']
            },
            {
              id: 'ai-tech-2',
              type: 'typography' as const,
              title: 'Sans-serif Typography',
              description: 'Clean, readable fonts for tech communication',
              fontFamily: 'Inter',
              tags: ['clean', 'readable', 'modern']
            }
          ]
        },
        'Healthcare': {
          styleKeywords: ['Professional', 'Trustworthy', 'Clean', 'Calming'],
          colorPalette: ['#059669', '#10b981', '#6ee7b7', '#1f2937', '#f9fafb'],
          items: [
            {
              id: 'ai-health-1',
              type: 'color' as const,
              title: 'Healing Green',
              description: 'Calming green colors associated with health',
              color: '#059669',
              tags: ['green', 'calming', 'health']
            }
          ]
        }
      }

      const industry = brand.metadata?.industry || 'Technology'
      const suggestion = industryMoodBoards[industry as keyof typeof industryMoodBoards] || industryMoodBoards['Technology']

      setMoodBoard(prev => ({
        ...prev,
        styleKeywords: suggestion.styleKeywords,
        colorPalette: suggestion.colorPalette,
        items: [...prev.items, ...suggestion.items]
      }))
    } catch (error) {
      console.error('Error generating AI mood board:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const exportMoodBoard = () => {
    const moodBoardData = {
      brand: brand.name,
      moodBoard,
      exported: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(moodBoardData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${brand.name.toLowerCase().replace(/\s+/g, '-')}-mood-board.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const filteredItems = selectedCategory === 'all' 
    ? moodBoard.items 
    : moodBoard.items.filter(item => item.type === selectedCategory)

  if (isLoading && moodBoard.items.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 bg-blue-600 rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-slate-600">Loading mood board...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Mood Board Creator</h2>
          <p className="text-slate-600">Collect visual inspiration and define your brand aesthetic</p>
        </div>
        <div className="flex items-center space-x-3">
          {lastSaved && (
            <div className="flex items-center space-x-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Saved {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
          <Button 
            variant="outline"
            onClick={exportMoodBoard}
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            onClick={saveMoodBoardData}
            disabled={isSaving}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Mood Board'}
          </Button>
        </div>
      </div>

      {/* AI Mood Board Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            AI Mood Board Generator
          </CardTitle>
          <CardDescription>
            Get AI-powered visual inspiration based on your brand and industry
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <Button 
              onClick={generateAIMoodBoard}
              disabled={isLoading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {isLoading ? 'Generating...' : 'Generate AI Inspiration'}
            </Button>
            <span className="text-sm text-slate-600">
              Based on {brand.metadata?.industry || 'Technology'} industry trends
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mood Board Settings */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Mood Board Settings</CardTitle>
            <CardDescription>
              Define the objective and style direction
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Board Title</Label>
              <Input
                value={moodBoard.title}
                onChange={(e) => setMoodBoard(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter mood board title..."
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={moodBoard.description}
                onChange={(e) => setMoodBoard(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the visual direction you're exploring..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Objective</Label>
              <Textarea
                value={moodBoard.objective}
                onChange={(e) => setMoodBoard(prev => ({ ...prev, objective: e.target.value }))}
                placeholder="What do you want to achieve with this mood board?"
                rows={2}
              />
            </div>

            <Separator />

            {/* Style Keywords */}
            <div className="space-y-3">
              <Label>Style Keywords</Label>
              <div className="flex flex-wrap gap-2">
                {STYLE_KEYWORDS.map(keyword => (
                  <Button
                    key={keyword}
                    size="sm"
                    variant={moodBoard.styleKeywords.includes(keyword) ? "default" : "outline"}
                    onClick={() => toggleStyleKeyword(keyword)}
                    className="h-7 px-3 text-xs"
                  >
                    {keyword}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Selected: {moodBoard.styleKeywords.join(', ') || 'None'}
              </p>
            </div>

            <Separator />

            {/* Color Palette */}
            <div className="space-y-3">
              <Label>Color Palette</Label>
              <div className="flex flex-wrap gap-2">
                {moodBoard.colorPalette.map((color, index) => (
                  <div key={index} className="relative group">
                    <div
                      className="w-8 h-8 rounded border-2 border-slate-200 cursor-pointer"
                      style={{ backgroundColor: color }}
                      onClick={() => removeColorFromPalette(color)}
                    />
                    <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                        <X className="w-2 h-2" />
                      </button>
                    </div>
                  </div>
                ))}
                <input
                  type="color"
                  onChange={(e) => addColorToPalette(e.target.value)}
                  className="w-8 h-8 rounded border-2 border-dashed border-slate-300 cursor-pointer"
                  title="Add color"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mood Board Items */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Visual Inspiration</CardTitle>
                <CardDescription>
                  Collect and organize visual references
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-1 border rounded text-sm"
                >
                  <option value="all">All Items</option>
                  <option value="image">Images</option>
                  <option value="color">Colors</option>
                  <option value="typography">Typography</option>
                  <option value="texture">Textures</option>
                  <option value="inspiration">Inspiration</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Add New Item */}
            <Card className="mb-6 border-dashed">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Title</Label>
                      <Input
                        value={newItem.title || ''}
                        onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Give this inspiration a title..."
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Type</Label>
                      <select
                        value={newItem.type || 'inspiration'}
                        onChange={(e) => setNewItem(prev => ({ ...prev, type: e.target.value as MoodBoardItem['type'] }))}
                        className="w-full px-2 py-1 border rounded text-sm h-8"
                      >
                        <option value="inspiration">Inspiration</option>
                        <option value="image">Image</option>
                        <option value="color">Color</option>
                        <option value="typography">Typography</option>
                        <option value="texture">Texture</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Textarea
                        value={newItem.description || ''}
                        onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe why this inspires you..."
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    
                    {newItem.type === 'image' && (
                      <div className="space-y-1">
                        <Label className="text-xs">Upload Image</Label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="w-full text-xs"
                        />
                      </div>
                    )}
                    
                    {newItem.type === 'color' && (
                      <div className="space-y-1">
                        <Label className="text-xs">Color</Label>
                        <input
                          type="color"
                          value={newItem.color || '#000000'}
                          onChange={(e) => setNewItem(prev => ({ ...prev, color: e.target.value }))}
                          className="w-full h-8 rounded border"
                        />
                      </div>
                    )}
                    
                    {newItem.type === 'typography' && (
                      <div className="space-y-1">
                        <Label className="text-xs">Font Family</Label>
                        <Input
                          value={newItem.fontFamily || ''}
                          onChange={(e) => setNewItem(prev => ({ ...prev, fontFamily: e.target.value }))}
                          placeholder="e.g., Inter, Roboto, Playfair Display"
                          className="h-8"
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Tags */}
                <div className="mt-3 space-y-2">
                  <Label className="text-xs">Tags</Label>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {newItem.tags?.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs h-5">
                        {tag}
                        <button
                          onClick={() => removeTagFromItem(index)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-2 h-2" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex space-x-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTagToItem(newTag)}
                      placeholder="Add tag..."
                      className="h-7 text-xs"
                    />
                    <Button
                      size="sm"
                      onClick={() => addTagToItem(newTag)}
                      className="h-7 px-2"
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4">
                  <Button
                    onClick={addMoodBoardItem}
                    disabled={!newItem.title?.trim()}
                    size="sm"
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Mood Board
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Mood Board Items Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <div className="aspect-square bg-slate-50 relative">
                    {item.type === 'image' && item.preview && (
                      <img 
                        src={item.preview} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {item.type === 'color' && (
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: item.color }}
                      >
                        <span className="text-white font-mono text-sm bg-black/20 px-2 py-1 rounded">
                          {item.color}
                        </span>
                      </div>
                    )}
                    {item.type === 'typography' && (
                      <div className="w-full h-full flex items-center justify-center p-4">
                        <div 
                          className="text-center"
                          style={{ fontFamily: item.fontFamily }}
                        >
                          <div className="text-2xl mb-2">Aa</div>
                          <div className="text-xs text-slate-600">{item.fontFamily}</div>
                        </div>
                      </div>
                    )}
                    {(item.type === 'texture' || item.type === 'inspiration') && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center text-slate-400">
                          <Sparkles className="w-8 h-8 mx-auto mb-2" />
                          <span className="text-xs">Inspiration</span>
                        </div>
                      </div>
                    )}
                    
                    <button
                      onClick={() => removeMoodBoardItem(item.id)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  
                  <CardContent className="p-3">
                    <h4 className="font-medium text-sm mb-1">{item.title}</h4>
                    <p className="text-xs text-slate-600 mb-2">{item.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {item.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs h-4 px-1">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredItems.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="mb-2">No items in this category yet</p>
                <p className="text-sm">Add some visual inspiration to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
          <CardDescription>
            Record insights, ideas, and observations about your visual direction
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={moodBoard.notes}
            onChange={(e) => setMoodBoard(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add notes about your mood board, design decisions, or ideas for future exploration..."
            rows={4}
          />
        </CardContent>
      </Card>
    </div>
  )
}